export type PipelineStepStatus =
    | 'PENDING'
    | 'RUNNING'
    | 'OK'
    | 'EXTRACTED'
    | 'LOADED'
    | 'EXPORTED'
    | 'SKIPPED'
    | 'FAILED';
export type PipelinePhase = 'init' | 'discovered' | 'executing' | 'completed' | 'failed';
export type PipelineStepType = 'extraction' | 'parse' | 'transform' | 'load' | 'export' | 'unknown';

export interface PipelineStepWarning {
    message: string;
    ruleType?: string;
    column?: string;
    columns?: string[];
}

export function formatWarningScope(w: PipelineStepWarning): string {
    const rule = w.ruleType ?? 'rule';
    if (w.column) return `${rule} · ${w.column}`;
    if (w.columns && w.columns.length > 0) return `${rule} · ${w.columns.join(', ')}`;
    return rule;
}

export interface PipelineStep {
    name: string;
    index: number;
    total: number;
    status: PipelineStepStatus;
    stepType: PipelineStepType;
    deps: string[];
    elapsedSeconds?: number;
    rowCount?: number;
    errorMessage?: string;
    skipReason?: string;
    description?: string;
    sourceInfo?: string;
    warnings?: PipelineStepWarning[];
    // Indexes in the run's logs of the step's start marker and of its end marker
    logStart?: number;
    logEnd?: number;
}

export interface PipelineState {
    hasPipelineData: boolean;
    executionOrder: string[];
    steps: Map<string, PipelineStep>;
    sourceFolderId?: number;
    totalDatasets?: number;
    totalElapsed?: number;
    totalExecuted?: number;
    totalSkipped?: number;
    phase: PipelinePhase;
    // Set when the pipeline fails outside a step
    pipelineError?: string;
}

const MARKER_PREFIX = 'PIPELINE_VISUALIZATION_MARKER';

export interface MarkerPayload {
    event: string;
    [key: string]: unknown;
}

// The engine writes step markers as PREFIX:{json}; the backend writes its dataset lines as PREFIX {json}
export function readMarker(line: string, separator: ':' | ' '): MarkerPayload | null {
    const prefix = MARKER_PREFIX + separator;
    const idx = line.indexOf(prefix);
    if (idx === -1) return null;
    try {
        const parsed = JSON.parse(line.slice(idx + prefix.length));
        if (parsed && typeof parsed === 'object' && typeof parsed.event === 'string') {
            return parsed as MarkerPayload;
        }
    } catch {
        // A malformed marker must not hide the run's other markers
    }
    return null;
}

const findMarker = (line: string) => readMarker(line, ':');

export function isPipelineMarker(line: string): boolean {
    return line.includes(MARKER_PREFIX);
}

// The statuses of a step that ran to its end
export const DONE_STATUSES: ReadonlySet<PipelineStepStatus> = new Set(['OK', 'EXTRACTED', 'LOADED', 'EXPORTED']);
// The done statuses of a step that leaves data to look at
export const DATA_STATUSES: ReadonlySet<PipelineStepStatus> = new Set(['OK', 'EXTRACTED']);

export function hasPipelineMarkers(logs: readonly string[]): boolean {
    return logs.some((line) => findMarker(line) !== null);
}

// The lines a person reads; the markers are drawn as the graph
export function runLogLines(logs: readonly string[]): string[] {
    return logs.filter((line) => !isPipelineMarker(line));
}

// A line that only separates sections ends at its prefix, such as "[...]: Job data: "
function isBlankLogLine(line: string): boolean {
    return line.replace(/[\s\u200b]/g, '').endsWith(':');
}

// The lines a step wrote between its start and its end; a running step has no end yet
export function stepLogLines(logs: readonly string[], step: PipelineStep): string[] {
    if (step.logStart === undefined) return [];
    return runLogLines(logs.slice(step.logStart + 1, step.logEnd ?? logs.length)).filter(
        (line) => !isBlankLogLine(line)
    );
}

function mapStepType(raw: unknown): PipelineStepType {
    if (raw === 'extract') return 'extraction';
    if (raw === 'parse') return 'parse';
    if (raw === 'load') return 'load';
    if (raw === 'transform') return 'transform';
    if (raw === 'export') return 'export';
    return 'unknown';
}

function parseWarnings(raw: unknown): PipelineStepWarning[] {
    if (!Array.isArray(raw)) return [];
    const out: PipelineStepWarning[] = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object') continue;
        const obj = item as Record<string, unknown>;
        if (typeof obj.message !== 'string') continue;
        const w: PipelineStepWarning = { message: obj.message };
        if (typeof obj.rule_type === 'string') w.ruleType = obj.rule_type;
        if (typeof obj.column === 'string') w.column = obj.column;
        if (Array.isArray(obj.columns)) w.columns = obj.columns.map(String);
        out.push(w);
    }
    return out;
}

export function parsePipelineLogs(logs: string[]): PipelineState {
    const state: PipelineState = {
        hasPipelineData: false,
        executionOrder: [],
        steps: new Map(),
        phase: 'init',
    };

    // Buffer for dataset metadata emitted before `execution_order` creates steps.
    const pendingDeps = new Map<string, string[]>();
    const pendingTypes = new Map<string, PipelineStepType>();
    const pendingDescriptions = new Map<string, string>();
    const pendingSourceInfo = new Map<string, string>();

    for (const [lineIndex, rawLine] of logs.entries()) {
        const marker = findMarker(rawLine);
        if (!marker) continue;
        state.hasPipelineData = true;

        switch (marker.event) {
            case 'pipeline_started': {
                if (typeof marker.source_folder_id === 'number') {
                    state.sourceFolderId = marker.source_folder_id;
                }
                break;
            }
            case 'dataset_registered': {
                const name = String(marker.name);
                const stepType = mapStepType(marker.type);
                const deps = Array.isArray(marker.deps) ? marker.deps.map(String) : [];
                pendingDeps.set(name, deps);
                pendingTypes.set(name, stepType);
                if (typeof marker.description === 'string') pendingDescriptions.set(name, marker.description);
                if (typeof marker.source_info === 'string') pendingSourceInfo.set(name, marker.source_info);
                const step = state.steps.get(name);
                if (step) {
                    step.deps = deps;
                    step.stepType = stepType;
                    if (typeof marker.description === 'string') step.description = marker.description;
                    if (typeof marker.source_info === 'string') step.sourceInfo = marker.source_info;
                }
                break;
            }
            case 'datasets_discovered': {
                if (typeof marker.total === 'number') state.totalDatasets = marker.total;
                state.phase = 'discovered';
                break;
            }
            case 'execution_order': {
                const order = Array.isArray(marker.order) ? marker.order.map(String) : [];
                state.executionOrder = order;
                state.phase = 'executing';
                const total = order.length;
                for (let i = 0; i < total; i++) {
                    const name = order[i];
                    state.steps.set(name, {
                        name,
                        index: i + 1,
                        total,
                        status: 'PENDING',
                        stepType: pendingTypes.get(name) ?? 'unknown',
                        deps: pendingDeps.get(name) ?? [],
                        description: pendingDescriptions.get(name),
                        sourceInfo: pendingSourceInfo.get(name),
                    });
                }
                break;
            }
            case 'step_started': {
                const name = String(marker.name);
                const stepType = mapStepType(marker.type);
                const index = typeof marker.index === 'number' ? marker.index : 0;
                const total = typeof marker.total === 'number' ? marker.total : 0;
                const existing = state.steps.get(name);
                if (existing) {
                    existing.index = index;
                    existing.total = total;
                    existing.status = 'RUNNING';
                    existing.stepType = stepType;
                    existing.logStart = lineIndex;
                } else {
                    state.steps.set(name, {
                        name,
                        index,
                        total,
                        status: 'RUNNING',
                        stepType,
                        deps: pendingDeps.get(name) ?? [],
                        description: pendingDescriptions.get(name),
                        sourceInfo: pendingSourceInfo.get(name),
                        logStart: lineIndex,
                    });
                }
                break;
            }
            case 'step_completed': {
                const name = String(marker.name);
                const step = state.steps.get(name);
                if (step) {
                    step.logEnd = lineIndex;
                    const rawStatus = marker.status as PipelineStepStatus;
                    if (DONE_STATUSES.has(rawStatus)) step.status = rawStatus;
                    if (typeof marker.rows === 'number') step.rowCount = marker.rows;
                    if (typeof marker.elapsed_seconds === 'number') step.elapsedSeconds = marker.elapsed_seconds;
                    const warnings = parseWarnings(marker.warnings);
                    if (warnings.length > 0) step.warnings = warnings;
                }
                break;
            }
            case 'step_failed': {
                const name = String(marker.name);
                const errorMsg = typeof marker.error === 'string' ? marker.error : undefined;
                const existing = state.steps.get(name);
                if (existing) {
                    existing.status = 'FAILED';
                    existing.logEnd = lineIndex;
                    if (errorMsg !== undefined) existing.errorMessage = errorMsg;
                } else {
                    // Keep the error of a step that never started
                    state.steps.set(name, {
                        name,
                        index: 0,
                        total: 0,
                        status: 'FAILED',
                        stepType: 'unknown',
                        deps: [],
                        errorMessage: errorMsg,
                    });
                }
                state.phase = 'failed';
                break;
            }
            case 'step_skipped': {
                const name = String(marker.name);
                const reason = typeof marker.reason === 'string' ? marker.reason : undefined;
                const existing = state.steps.get(name);
                if (existing) {
                    existing.status = 'SKIPPED';
                    existing.logEnd = lineIndex;
                    if (reason !== undefined) existing.skipReason = reason;
                } else {
                    state.steps.set(name, {
                        name,
                        index: 0,
                        total: 0,
                        status: 'SKIPPED',
                        stepType: pendingTypes.get(name) ?? 'unknown',
                        deps: pendingDeps.get(name) ?? [],
                        skipReason: reason,
                        description: pendingDescriptions.get(name),
                        sourceInfo: pendingSourceInfo.get(name),
                    });
                }
                break;
            }
            case 'pipeline_failed': {
                if (typeof marker.error === 'string') state.pipelineError = marker.error;
                state.phase = 'failed';
                break;
            }
            case 'pipeline_completed': {
                if (typeof marker.executed === 'number') state.totalExecuted = marker.executed;
                if (typeof marker.skipped === 'number') state.totalSkipped = marker.skipped;
                if (typeof marker.elapsed_seconds === 'number') state.totalElapsed = marker.elapsed_seconds;
                state.phase = 'completed';
                break;
            }
        }
    }

    // Show the first pending step as running until step_started arrives
    if (state.phase === 'executing') {
        const hasRunning = Array.from(state.steps.values()).some((s) => s.status === 'RUNNING');
        if (!hasRunning) {
            for (const name of state.executionOrder) {
                const step = state.steps.get(name);
                if (step && step.status === 'PENDING') {
                    step.status = 'RUNNING';
                    break;
                }
            }
        }
    }

    return state;
}
